<template lang="pug">
    .neuro-start
        ._wrap
            ._hello {{ locale("hello") }}
            ._project(v-if="projectTitle") {{ locale("inProject", { title: projectTitle }) }}
            ._lead {{ locale("lead") }}

            ._cards
                ._card(v-for="g in groups" :key="g.cat" :class="{ on: cat === g.cat }" @click="setCat(g.cat)")
                    ._logos
                        img(v-for="m in g.models.slice(0, 4)" :key="m" :src="logo(m)" alt="")
                    ._card-title {{ g.title }}
                    ._card-desc {{ g.desc }}
                router-link._card-face(to="/analyze/")
                    ._face-ph
                    div
                        ._card-title {{ locale("faceTitle") }}
                        ._card-desc {{ locale("faceDesc") }}
                    ._face-go {{ locale("try") }}

            template(v-if="activeGroup")
                ._sec {{ activeGroup.title }}: {{ locale("pickModel") }}
                ._models
                    ._model(v-for="m in activeGroup.models" :key="m" :class="{ on: model === m, locked: familyLocked(m) }" @click="selectFamily(m)" :title="familyPlan(m) && familyPlan(m) !== 'free' ? locale('planHint', { plan: planLabel(familyPlan(m)) }) : null")
                        img(:src="logo(m)" alt="")
                        span._model-name {{ name(m) }}
                        //- Тариф, с которого открывается модель, виден сразу, а не только в списке версий
                        span._model-plan(v-if="familyPlan(m)" :class="{ free: familyPlan(m) === 'free' }") {{ planLabel(familyPlan(m)) }}
            template(v-else)
                ._sec {{ locale("examples") }}
                ._examples
                    ._example(v-for="e in examples" :key="e.text" @click="useExample(e)")
                        ._example-img(:style="{ backgroundImage: `url(${e.img})` }")
                        ._example-text {{ e.text }}
                            small {{ e.label }}

        ._bottom
            form._prompt(@submit.prevent="send()")
                textarea._input(ref="input" v-model="text" rows="1" :placeholder="locale('placeholder')" @input="grow" @keydown.enter.exact.prevent="send()")
                ._bar
                    ._picker(v-click-outside="closeMenu")
                        button._chip(type="button" @click="toggleMenu" :class="{ open: menuOpen }")
                            img(:src="logo(model)" alt="")
                            span._chip-name {{ currentLabel }}
                            svg._chip-chev(viewBox="0 0 24 24")
                                path(d="m6 15 6-6 6 6")
                        ._menu(v-if="menuOpen")
                            ._menu-tabs
                                button._menu-tab(v-for="g in groups" :key="g.cat" type="button" :class="{ on: menuCat === g.cat }" @click="menuCat = g.cat") {{ g.title }}
                            ._menu-list
                                template(v-for="f in menuFamilies")
                                    ._menu-family {{ name(f.family) }}
                                    button._menu-item(v-for="v in f.versions" :key="f.family + v.id" type="button" :class="{ on: isCurrent(f.family, v), locked: v.locked }" @click="pickVersion(f.family, v)")
                                        img(:src="logo(f.family)" alt="")
                                        span._menu-text
                                            span._menu-name {{ v.name }}
                                            small(v-if="v.credits") {{ creditsLabel(v.credits) }}
                                        span._menu-plan(v-if="v.locked") {{ v.plan }}
                                        svg._menu-check(v-else-if="isCurrent(f.family, v)" viewBox="0 0 24 24")
                                            path(d="m5 12 5 5 9-10")
                    //- Настройки есть у моделей, которые форма чата настраивает общим блоком; выбор уходит в чат вместе с запросом
                    ._settings(v-if="spec" v-click-outside="closeSettings")
                        button._settings-chip(type="button" :class="{ open: settingsOpen }" @click="toggleSettings" :title="locale('settings')")
                            svg(viewBox="0 0 24 24")
                                path(d="M4 7h10M18 7h2M4 17h2M10 17h10M14 5v4M8 15v4")
                            span._settings-sum {{ settingsSummary }}
                        ._pop(v-if="settingsOpen")
                            ._pop-title {{ currentLabel }}
                            ._pop-row
                                ._pop-label {{ locale("resolution") }}
                                ._pop-chips
                                    button._pop-chip(v-for="r in spec.resolutions" :key="r" type="button" :class="{ on: cur.resolution === r }" @click="setOpt('resolution', r)") {{ r }}
                            ._pop-row(v-if="spec.durations.length > 1")
                                ._pop-label {{ locale("duration") }}
                                ._pop-chips
                                    button._pop-chip(v-for="d in spec.durations" :key="d" type="button" :class="{ on: cur.duration === d }" @click="setOpt('duration', d)") {{ locale("sec", { n: d }) }}
                            ._pop-row(v-if="spec.aspects.length")
                                ._pop-label {{ locale("aspect") }}
                                ._pop-chips
                                    button._pop-chip(v-for="a in spec.aspects" :key="a" type="button" :class="{ on: cur.aspectRatio === a }" @click="setOpt('aspectRatio', a)") {{ a }}
                            ._pop-row(v-if="spec.audio")
                                ._pop-label {{ locale("sound") }}
                                ._pop-chips
                                    button._pop-chip(type="button" :class="{ on: cur.audio }" @click="setOpt('audio', true)") {{ locale("on") }}
                                    button._pop-chip(type="button" :class="{ on: !cur.audio }" @click="setOpt('audio', false)") {{ locale("off") }}
                            ._pop-price(v-if="price") {{ locale("price") }}: {{ priceLabel }}
                    ._sp
                    button._send(type="submit" :disabled="!text.trim()" :aria-label="locale('send')")
                        svg(viewBox="0 0 24 24")
                            path(d="M12 19V5M5 12l7-7 7 7")
</template>

<script>
import gql from "graphql-tag";
import getMySubscriptionsQuery from "@/graphql/queries/getMySubscriptions.query.graphql";
import { getThemeLogo, themeDisplayName } from "@/utils/ai-theme-logo";
import { OPEN_ROUTER_MODELS, REPLICATE_MODELS, creditsLabel as formatCredits } from "@/utils/modelCatalog";
import { VIDEO_OPTIONS, IMAGE_OPTIONS, GENERIC_VIDEO_MODELS, GENERIC_VIDEO_ASPECTS, IMAGE_ASPECTS, normalizeOptions, durationsFor, creditsForOptions } from "@/utils/generationOptions";

const GROUPS = [
    { cat: "text", desc: "ChatGPT, Claude, Gemini, DeepSeek",
        models: ["ChatGPT", "Anthropic Claude", "Google Gemini", "DeepSeek", "X-AI Grok", "Qwen", "Mistral AI", "Meta Llama"] },
    { cat: "image", desc: "Midjourney, Nano Banana, Recraft",
        models: ["Gemini Nano Banana", "Google Imagen 4", "Midjorney", "Recraft", "Flux", "Ideogram", "Seedream", "DALL-E"] },
    { cat: "video", desc: "Veo, Kling, Seedance, Wan, Luma",
        models: ["Google Veo 3", "Kling", "Seedance 1 Pro", "Wan", "Luma", "Happy Horse", "Hailuo", "Pixverse", "Grok Imagine", "Runway Gen-4"] },
];

// Ключи localStorage, из которых шапка чата берёт выбранную версию модели
const VERSION_KEY = { gpt: "selectedGptModel", openrouter: "selectedOpenRouterModel", replicate: "selectedReplicateModel" };

export default {
    name: "NeuroStart",
    data: () => ({
        text: "",
        model: "Anthropic Claude",
        version: null,
        menuOpen: false,
        menuCat: "text",
        userPlan: "free",
        settingsOpen: false,
        // Выбор в попапе настроек; пустые поля заменяются значениями модели по умолчанию
        opts: {},
    }),
    computed: {
        groups() {
            return GROUPS.map((g) => ({ ...g, title: this.locale(`cat_${g.cat}`) }));
        },
        examples() {
            return [
                { text: this.locale("example1"), label: `${this.locale("cat_video")} · Kling`, model: "Kling", img: require("@/assets/images/ai-pika-examples/1.webp") },
                { text: this.locale("example2"), label: `${this.locale("cat_image")} · Nano Banana`, model: "Gemini Nano Banana", img: require("@/assets/images/ai-hero/midjourney.webp") },
                { text: this.locale("example3"), label: `${this.locale("cat_video")} · Seedance`, model: "Seedance 1 Pro", img: require("@/assets/images/ai-pika-examples/3.webp") },
            ];
        },
        cat() {
            return this.$route.query.cat || "";
        },
        // Название проекта, если чат создаётся из «+» у проекта (/new?project=<id>)
        projectTitle() {
            const project = this.$store.state.shellProjects.find((p) => +p.id === +this.$route.query.project);
            return project ? project.title : "";
        },
        activeGroup() {
            return this.groups.find((g) => g.cat === this.cat) || null;
        },
        // Категория выбранной нейросети: текст, картинка или видео
        modelCat() {
            const g = this.groups.find((x) => x.models.includes(this.model));
            return g ? g.cat : "text";
        },
        // Подпись на кнопке выбора: версия, если она известна, иначе название семейства
        currentLabel() {
            const v = this.versionsOf(this.model).find((x) => x.id === this.version);
            return v ? v.name : this.name(this.model);
        },
        // Что можно настроить у выбранной версии. null: у версии нет настроек, которые форма чата примет со стартового экрана
        spec() {
            const m = this.version;
            if (GENERIC_VIDEO_MODELS.includes(m)) {
                const cfg = VIDEO_OPTIONS[m];
                const resolution = normalizeOptions(m, this.opts).resolution;
                return { resolutions: cfg.resolutions, durations: durationsFor(m, resolution), audio: cfg.audio, aspects: GENERIC_VIDEO_ASPECTS[m] || [] };
            }
            if (IMAGE_OPTIONS[m]) {
                return { resolutions: IMAGE_OPTIONS[m].resolutions, durations: [], audio: false, aspects: IMAGE_ASPECTS };
            }
            return null;
        },
        cur() {
            if (!this.spec) return {};
            const options = normalizeOptions(this.version, this.opts);
            if (this.spec.aspects.length) {
                options.aspectRatio = this.spec.aspects.includes(this.opts.aspectRatio) ? this.opts.aspectRatio : this.spec.aspects[0];
            }
            return options;
        },
        price() {
            return this.spec ? creditsForOptions(this.version, this.cur) : null;
        },
        priceLabel() {
            const n = this.price;
            const form = n % 10 === 1 && n % 100 !== 11 ? 1 : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 2 : 5;
            return `${n} ${this.locale(`credits_${form}`)}`;
        },
        // Подпись на кнопке: выбранные значения через точку
        settingsSummary() {
            const c = this.cur;
            return [c.resolution, c.duration && this.locale("sec", { n: c.duration }), c.aspectRatio].filter(Boolean).join(" · ");
        },
        // В меню только нейросети выбранной категории, у каждой свои версии
        menuFamilies() {
            const g = this.groups.find((x) => x.cat === this.menuCat);
            return g ? g.models.map((family) => ({ family, versions: this.versionsOf(family) })) : [];
        },
    },
    watch: {
        // У другой версии свои допустимые значения: выбор начинается заново
        version() {
            this.opts = {};
            this.settingsOpen = false;
        },
        cat: {
            immediate: true,
            handler(cat) {
                const g = this.groups.find((x) => x.cat === cat);
                if (g && !g.models.includes(this.model)) this.pickFamily(g.models[0]);
            },
        },
    },
    mounted() {
        this.pickFamily(this.model);
        this.loadPlan();
    },
    methods: {
        creditsLabel(text) {
            return formatCredits(text, this.$i18n.locale);
        },
        async loadPlan() {
            try {
                const r = await this.$apollo.query({ query: gql(getMySubscriptionsQuery) });
                const plan = r.data.getMySubscriptions.find((p) => [26, 27, 32, 33, 34].includes(+p.id));
                this.userPlan = plan && plan.level ? plan.level.toLowerCase() : "free";
            } catch (e) {
                console.log(e);
            }
        },
        // Версии семейства из общего каталога. У Midjourney и DALL-E версий нет, они идут одной строкой
        versionsOf(family) {
            const cat = (this.groups.find((g) => g.models.includes(family)) || {}).cat;
            const list = (cat === "text" ? OPEN_ROUTER_MODELS : REPLICATE_MODELS)[family] || [];
            if (!list.length) return [{ id: null, name: this.name(family), locked: false }];
            return list.map((v) => {
                const level = ["plus", "gold", "platinum"].find((l) => v.availableFor.includes(l)) || "";
                const locked = !v.availableFor.includes(this.userPlan);
                return { id: v.id, name: v.name, credits: v.credits, locked, level, plan: level ? level[0].toUpperCase() + level.slice(1) : "" };
            });
        },
        // Самый дешёвый тариф, на котором открывается хотя бы одна версия семейства ("" если в каталоге нет данных)
        familyPlan(family) {
            const cat = (this.groups.find((g) => g.models.includes(family)) || {}).cat;
            const list = (cat === "text" ? OPEN_ROUTER_MODELS : REPLICATE_MODELS)[family] || [];
            return ["free", "plus", "gold", "platinum"].find((l) => list.some((v) => (v.availableFor || []).includes(l))) || "";
        },
        planLabel(level) {
            return level === "free" ? this.locale("planFree") : level[0].toUpperCase() + level.slice(1);
        },
        // Ни одна версия семейства не открыта на тарифе пользователя
        familyLocked(family) {
            return this.versionsOf(family).every((v) => v.locked);
        },
        // Клик по карточке модели. pickFamily вызывается и из кода, поэтому событие только здесь
        selectFamily(family) {
            this.track("model_select", {
                model: family,
                category: this.activeGroup ? this.activeGroup.cat : "",
                tariff_badge: this.familyPlan(family) || "free",
                source: "start_card",
            });
            this.pickFamily(family);
        },
        // Семейство выбрано без версии: берём первую доступную на тарифе
        pickFamily(family) {
            this.model = family;
            const open = this.versionsOf(family).find((v) => !v.locked);
            this.version = open ? open.id : null;
        },
        isCurrent(family, v) {
            return this.model === family && this.version === v.id;
        },
        pickVersion(family, v) {
            this.track("model_select", {
                model: v.name || family,
                category: this.modelCat,
                tariff_badge: v.locked ? (v.level || "").toLowerCase() : "available",
                source: "start_menu",
            });
            if (v.locked) {
                this.$modal.show("AISubscriptions", { level: v.level.toUpperCase(), model: v.name });
                return;
            }
            this.model = family;
            this.version = v.id;
            this.menuOpen = false;
            // Страница показывает ту же категорию, что и выбранная нейросеть
            if (this.cat && this.cat !== this.modelCat) this.setCat(this.modelCat);
        },
        toggleMenu() {
            this.menuOpen = !this.menuOpen;
            if (this.menuOpen) this.menuCat = this.modelCat;
        },
        closeMenu() {
            this.menuOpen = false;
        },
        // Поле растёт вверх до трети экрана, дальше прокручивается внутри
        grow() {
            const el = this.$refs.input;
            if (!el) return;
            const max = Math.round(window.innerHeight / 3);
            el.style.height = "auto";
            el.style.height = Math.min(el.scrollHeight, max) + "px";
            el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
        },
        // Выбранная версия запоминается там же, где её хранит шапка чата
        persistVersion() {
            if (!this.version) return;
            const key = this.model === "ChatGPT" ? VERSION_KEY.gpt : this.modelCat === "text" ? VERSION_KEY.openrouter : VERSION_KEY.replicate;
            localStorage.setItem(key, this.version);
        },
        setOpt(key, value) {
            this.track("setting_changed", { setting: key, value: String(value), model: this.version || this.model, source: "start" });
            this.opts = { ...this.cur, [key]: value };
        },
        toggleSettings() {
            this.settingsOpen = !this.settingsOpen;
            if (this.settingsOpen) this.track("setting_open", { setting: "generation", model: this.version || this.model, source: "start" });
        },
        closeSettings() {
            this.settingsOpen = false;
        },
        logo: (m) => getThemeLogo(m),
        name: (m) => themeDisplayName(m),
        setCat(cat) {
            // project (новый чат в проекте) сохраняем при смене категории
            const query = cat === this.cat ? {} : { cat };
            if (this.$route.query.project) query.project = this.$route.query.project;
            this.$router.replace({ query }).catch(() => {});
        },
        useExample(e) {
            this.track("example_use", { model: e.model, category: this.cat });
            this.text = e.text;
            this.pickFamily(e.model);
            this.$nextTick(this.grow);
        },
        send() {
            if (!this.text.trim()) return;
            this.go(true);
        },
        go(autoSend) {
            this.persistVersion();
            this.$store.dispatch("set", { name: "shellPendingSend", value: autoSend });
            this.$store.dispatch("set", { name: "shellPendingOptions", value: this.spec ? { model: this.version, ...this.cur } : null });
            if (this.text.trim()) this.$store.dispatch("set", { name: "shellPendingPrompt", value: this.text.trim() });
            const query = { open: this.model, new: "1" };
            if (this.$route.query.project) query.project = this.$route.query.project;
            this.$router.push({ path: `/messages/${this.MERLIN_CONVERSATION_ID}`, query });
        },
    },
};
</script>

<style lang="scss" scoped>
@import "~@/styles/_variables.scss";
@import "~@/styles/_shell-tokens.scss";

// Экран ровно во всю высоту: карточки и модели прокручиваются сами, поле ввода остаётся внизу и растёт вверх
.neuro-start {
    height: 100vh; height: 100dvh; display: flex; flex-direction: column; align-items: center; padding: 40px 30px 0; color: #ececec;
    @media (max-width: 991px) { height: calc(100dvh - 52px); padding: 20px 14px 0; }

    &__wrap { width: 100%; max-width: 860px; flex: 1; min-height: 0; overflow-y: auto; }
    &__hello { text-align: center; font-size: 34px; font-weight: 700; margin: 10px 0 6px; @media (max-width: 991px) { font-size: 24px; } }
    &__project { text-align: center; color: $gold; font-size: 14px; margin: 0 0 10px; }
    &__lead { text-align: center; color: $text-gray; margin-bottom: 26px; @media (max-width: 991px) { display: none; } }

    &__cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
    &__card, &__card-face { background: #222; border: 1px solid $sh-line; border-radius: $sh-r-card; padding: 16px; cursor: pointer; color: inherit; text-decoration: none;
        &:hover, &.on { border-color: #4a4433; }
        @media (max-width: 991px) { padding: 10px; } }
    &__logos { display: flex; gap: 4px; img { width: 22px; height: 22px; border-radius: 6px; object-fit: cover; }
        @media (max-width: 991px) { display: none; } }
    &__card-title { font-size: 16px; font-weight: 700; margin: 10px 0 4px; @media (max-width: 991px) { font-size: 13px; margin: 0; } }
    &__card-desc { color: $text-gray; font-size: 13px; @media (max-width: 991px) { display: none; } }
    &__card-face { grid-column: span 3; display: flex; align-items: center; gap: 16px; padding: 14px 16px; background: linear-gradient(90deg, #262318, #222 60%); border-color: #3d3826;
        .neuro-start__card-title { margin: 0 0 2px; } }
    &__face-ph { width: 46px; height: 46px; border-radius: $sh-r-control; flex: none; background: url("~@/assets/images/anfas-example.jpg") center / cover; }
    &__face-go { margin-left: auto; color: $gold; font-weight: 600; white-space: nowrap; @media (max-width: 991px) { display: none; } }

    &__sec { color: $text-gray; font-size: 13px; margin: 22px 0 10px; }
    // minmax(0, 1fr) и min-width: 0 у плитки: иначе длинное название плюс бейдж тарифа растягивает колонку шире экрана и правый ряд обрезается
    &__models { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; @media (max-width: 991px) { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
    &__model { display: flex; align-items: center; gap: 8px; min-width: 0; background: #222; border: 1px solid #2f2f2f; border-radius: $sh-r-control; padding: 9px 10px; cursor: pointer; font-size: 13.5px;
        &.on { border-color: $gold-alt; color: #fff; }
        img { width: 22px; height: 22px; border-radius: 6px; object-fit: cover; flex: none; }
        &.locked { color: $sh-text-2; }
        &.locked img { opacity: .6; } }
    &__model-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    &__model-plan { flex: none; font-size: 11px; color: $gold; border: 1px solid #4a4433; border-radius: $sh-r-pill; padding: 1px 8px;
        &.free { color: #8fd19e; border-color: #2f4a37; } }

    &__examples { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; @media (max-width: 991px) { grid-template-columns: 1fr 1fr; } }
    &__example { border-radius: $sh-r-card; overflow: hidden; background: #222; border: 1px solid #2f2f2f; cursor: pointer; }
    &__example-img { height: 104px; background-size: cover; background-position: center; position: relative;
        span { position: absolute; left: 8px; top: 8px; background: rgba(0, 0, 0, .6); font-size: 11px; padding: 3px 9px; border-radius: $sh-r-pill; } }
    &__example-text { padding: 9px 12px 10px; font-size: 13px; color: #d6d6d6;
        small { display: block; margin-top: 2px; font-size: 12px; color: $sh-text-3; } }

    &__bottom { flex: none; width: 100%; max-width: 860px; padding: 12px 0 22px; }
    &__prompt { background: $sh-surface; border: 1px solid $sh-line-strong; border-radius: $sh-r-card; padding: 14px 14px 10px; box-shadow: 0 10px 40px rgba(0, 0, 0, .35);
        &:focus-within { border-color: #4a4433; } }
    &__input { display: block; width: 100%; min-height: 28px; max-height: 33vh; background: none; border: 0; outline: none; resize: none; overflow-y: hidden; color: #ececec; font: inherit; font-size: 15px; line-height: 1.5; margin-bottom: 8px; }
    &__bar { position: relative; display: flex; align-items: center; gap: 8px; }

    &__picker { position: relative; }
    &__chip, &__settings-chip { display: flex; align-items: center; gap: 6px; height: 34px; background: none; color: $sh-text-2; font: inherit; font-size: 13px; cursor: pointer;
        border: 1px solid $sh-line-strong; border-radius: $sh-r-pill; padding: 0 12px;
        &:hover, &.open { background: $sh-surface-2; color: $sh-text; }
        img { width: 16px; height: 16px; border-radius: 4px; object-fit: cover; }
        svg { width: 14px; height: 14px; stroke: currentColor; fill: none; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; } }
    &__chip-name { max-width: 190px; @media (max-width: 480px) { max-width: 120px; } overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    &__chip-chev { opacity: .7; }

    // Меню версий открывается вверх, как в редакторе кода
    &__menu { position: absolute; left: 0; bottom: calc(100% + 8px); z-index: 40; width: 340px; max-width: calc(100vw - 28px);
        background: $sh-surface-2; border: 1px solid $sh-line-strong; border-radius: 16px; box-shadow: $sh-shadow-pop; padding: 6px; }
    &__menu-tabs { display: flex; gap: 4px; padding: 2px 2px 6px; border-bottom: 1px solid $sh-line; }
    &__menu-tab { flex: 1; height: 30px; border: 0; border-radius: $sh-r-pill; background: none; color: $sh-text-2; font: inherit; font-size: 13px; cursor: pointer;
        &:hover { color: $sh-text; }
        &.on { background: $sh-surface-3; color: $sh-text; } }
    &__menu-list { max-height: min(52vh, 420px); overflow-y: auto; padding-top: 4px; }
    &__menu-family { padding: 10px 10px 4px; font-size: 11px; letter-spacing: .4px; text-transform: uppercase; color: $sh-text-3; }
    &__menu-item { display: flex; align-items: center; gap: 10px; width: 100%; padding: 7px 10px; border: 0; border-radius: 10px; background: none; color: $sh-text; font: inherit; text-align: left; cursor: pointer;
        &:hover { background: $sh-surface-3; }
        &.on { background: $sh-surface-3; }
        &.locked { color: $sh-text-2; }
        img { width: 20px; height: 20px; border-radius: 5px; object-fit: cover; flex: none; } }
    &__menu-text { display: flex; flex-direction: column; min-width: 0; flex: 1;
        small { color: $sh-text-3; font-size: 11.5px; margin-top: 1px; } }
    &__menu-name { font-size: 13.5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    &__menu-plan { flex: none; font-size: 11px; color: $gold; border: 1px solid #4a4433; border-radius: $sh-r-pill; padding: 1px 8px; }
    &__menu-check { flex: none; width: 16px; height: 16px; stroke: $gold; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }

    &__settings-sum { max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; @media (max-width: 480px) { max-width: 96px; } }
    // Попап настроек привязан к полосе кнопок, чтобы на телефоне не уходил за край экрана
    &__pop { position: absolute; left: 0; bottom: calc(100% + 8px); z-index: 40; width: 340px; max-width: 100%;
        background: $sh-surface-2; border: 1px solid $sh-line-strong; border-radius: 16px; box-shadow: $sh-shadow-pop; padding: 14px; }
    &__pop-title { font-size: 13.5px; font-weight: 600; margin-bottom: 4px; }
    &__pop-row { margin-top: 10px; }
    &__pop-label { font-size: 12px; color: $sh-text-3; margin-bottom: 6px; }
    &__pop-chips { display: flex; flex-wrap: wrap; gap: 6px; }
    &__pop-chip { height: 30px; padding: 0 12px; border: 1px solid $sh-line-strong; border-radius: $sh-r-pill; background: none; color: $sh-text-2; font: inherit; font-size: 13px; cursor: pointer;
        &:hover { color: $sh-text; background: $sh-surface-3; }
        &.on { border-color: $sh-accent; color: $sh-text; background: rgba($sh-accent, .08); } }
    &__pop-price { margin-top: 14px; padding-top: 10px; border-top: 1px solid $sh-line; font-size: 13px; color: $sh-text-2; }

    &__sp { flex: 1; }
    &__send { flex: none; width: 36px; height: 36px; border-radius: 50%; background: $gold; color: #141414; border: 0; display: grid; place-items: center; cursor: pointer;
        &:disabled { opacity: .4; cursor: default; }
        svg { width: 18px; height: 18px; stroke: currentColor; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; } }
}
</style>
